import { useEffect, useLayoutEffect, useState } from "react";
import { apiFetchEncryptedBlob, apiFetchWorkspaceBlob } from "../lib/authApi";
import { readChatImageCached, writeChatImageCached } from "../lib/chatImageDiskCache";
import {
  b64ToU8,
  decryptImageBytes,
  isE2eImageAttachment,
  isWorkspaceServerImageAttachment,
  type ChatImageAttachmentMeta,
} from "../lib/crypto";
import { ChatImageLightbox } from "./ChatImageLightbox";

type MessageImageBodyProps = {
  attachment: ChatImageAttachmentMeta;
  /** When set (vault), used instead of GET /api/uploads/:id for ciphertext. */
  fetchE2eEncryptedBlob?: (blobId: string) => Promise<ArrayBuffer>;
};

function imageCacheKey(attachment: ChatImageAttachmentMeta, vault: boolean): string {
  if (isWorkspaceServerImageAttachment(attachment)) {
    return `ws:${attachment.workspace_id}:${attachment.blob_id}`;
  }
  return vault ? `vaultimg:${attachment.blob_id}` : attachment.blob_id;
}

export function MessageImageBody({ attachment, fetchE2eEncryptedBlob }: MessageImageBodyProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const cacheKey = imageCacheKey(attachment, Boolean(fetchE2eEncryptedBlob));

  /** Clear blob src before passive effect cleanup revokes the URL (avoids blob: load errors in the console). */
  useLayoutEffect(() => {
    setUrl(null);
    setError(null);
  }, [cacheKey]);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const assignUrl = (u: string) => {
      if (cancelled) {
        URL.revokeObjectURL(u);
        return;
      }
      objectUrl = u;
      setUrl(u);
    };

    void (async () => {
      try {
        const cachedPlain = await readChatImageCached(cacheKey);
        if (cachedPlain) {
          const blob = new Blob([new Uint8Array(cachedPlain)], { type: attachment.mime });
          assignUrl(URL.createObjectURL(blob));
          return;
        }

        if (isWorkspaceServerImageAttachment(attachment)) {
          const buf = await apiFetchWorkspaceBlob(attachment.workspace_id, attachment.blob_id);
          const plain = new Uint8Array(buf);
          void writeChatImageCached(cacheKey, attachment.mime, plain);
          const blob = new Blob([plain], { type: attachment.mime });
          assignUrl(URL.createObjectURL(blob));
          return;
        }

        if (!isE2eImageAttachment(attachment)) {
          if (!cancelled) setError("не удалось показать изображение");
          return;
        }

        const buf = fetchE2eEncryptedBlob
          ? await fetchE2eEncryptedBlob(attachment.blob_id)
          : await apiFetchEncryptedBlob(attachment.blob_id);
        const key = b64ToU8(attachment.key_b64);
        const nonce = b64ToU8(attachment.nonce_b64);
        const plain = decryptImageBytes(new Uint8Array(buf), nonce, key);
        void writeChatImageCached(cacheKey, attachment.mime, plain);
        const blob = new Blob([new Uint8Array(plain)], { type: attachment.mime });
        assignUrl(URL.createObjectURL(blob));
      } catch {
        if (!cancelled) setError("не удалось показать изображение");
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, cacheKey, fetchE2eEncryptedBlob]);

  const reservedAspect =
    attachment.width > 0 && attachment.height > 0
      ? `${attachment.width} / ${attachment.height}`
      : null;

  if (error) {
    return <span className="bubble__img-err">{error}</span>;
  }
  if (!url) {
    const sizeClass = reservedAspect ? "bubble__img-loading--sized" : "bubble__img-loading--default";
    return (
      <span
        className={`bubble__img-loading ${sizeClass}`}
        style={reservedAspect ? { aspectRatio: reservedAspect } : undefined}
      >
        …
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        className="bubble__img-btn"
        onClick={() => setLightboxOpen(true)}
        aria-label="Открыть изображение крупнее"
      >
        <img
          src={url}
          alt=""
          className="bubble__img"
          width={attachment.width}
          height={attachment.height}
          loading="lazy"
        />
      </button>
      <ChatImageLightbox open={lightboxOpen} onClose={() => setLightboxOpen(false)} url={url} />
    </>
  );
}
